import { Box } from "@primer/react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { RootState } from "../../../state/reducers";
import { actions } from "../../../state/actions/actionsWrapper";
import { crmHocSinhApi } from "../../../api/crmHocSinhApi";
import { truongApi } from "../../../api/truongApi";
import { hocSinhApi } from "../../../api/hocSinhApi";
import { ITruong } from "../../../models/response/truong/ITruong";
import Button from "../../../components/ui/button";
import { NotifyHelper } from "../../../helpers/toast";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { HocSinhFormSubmitRequest } from "../../../models/request/hoc-sinh/HocSinhFormSubmitRequest";
import { IHocSinh } from "../../../models/response/crm-hocsinh/IHocsinh";
import ThongTinDangKy from "./ThongTinDangKy";
import ThongTinDiaChi from "./ThongTinDiaChi";
import ThongTinHocSinh from "./ThongTinHocSinh";
import ThongTinKhac from "./ThongTinKhac";
import ThongTinLienHe from "./ThongTinLienHe";

interface PhuHuynh {
  ho_ten?: string;
  dien_thoai?: string;
  email?: string;
  dm_quanhe_id?: number;
}

interface ProfileFormProps {
  hocSinh: IHocSinh;
  phuHuynh?: PhuHuynh;
  onSubmitSuccess: (id: number) => void;
  height?: number;
  hideSaveButton?: boolean;
}

interface FormRef {
  handleSaveChanges: () => void;
}

const parseValidDate = (val: any): Date | null => {
  if (!val) return null;
  const m = moment(val);
  if (!m.isValid() || m.year() <= 1900) return null;
  return m.toDate();
};

const ProfileForm = forwardRef<FormRef, ProfileFormProps>(
  ({ hocSinh, onSubmitSuccess, hideSaveButton }, ref) => {
    const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
    const dispatch = useDispatch();
    const dm_khois = useSelector((state: RootState) => state.categorySource.dm_khois);
    const dm_cosos = useSelector((state: RootState) => state.categorySource.dm_cosos);
    const [allTruongs, setAllTruongs] = useState<ITruong[]>([]);
    const formContainerRef = useRef<HTMLFormElement | null>(null);

    const buildNormalizedData = useCallback((source: IHocSinh): IHocSinh => {
      const resolvedNamHoc = source?.nam_hoc || nam_hoc;
      const resolvedCoSoId = source?.dm_coso_id || dm_coso_id;
      const resolvedNamNhapHoc = source?.nam_nhap_hoc || (source?.id === 0 ? resolvedNamHoc : "");
      return {
        ...source,
        nam_hoc: resolvedNamHoc,
        dm_coso_id: resolvedCoSoId,
        nam_nhap_hoc: resolvedNamNhapHoc,
        ngay_sinh: parseValidDate(source?.ngay_sinh),
        ngay_nhap_hoc: parseValidDate(source?.ngay_nhap_hoc),
        ngaythi_dauvao_dukien: parseValidDate(source?.ngaythi_dauvao_dukien),
      };
    }, [nam_hoc, dm_coso_id]);

    const [isSaving, setIsSaving] = useState(false);
    const [isGeneratingCode, setIsGeneratingCode] = useState(false);
    const [formData, setFormData] = useState<IHocSinh>(() => buildNormalizedData(hocSinh));
    const {
      register,
      handleSubmit,
      control,
      trigger,
      setValue,
      reset,
      formState: { errors },
    } = useForm<IHocSinh>({ mode: "all", defaultValues: formData });

    useEffect(() => {
      const loadTruongs = async () => {
        try {
          const res = await truongApi.selectAll();
          if (res.is_success && res.data) {
            setAllTruongs(res.data);
          }
        } catch (error) {
          console.error("Error loading schools:", error);
        }
      };
      loadTruongs();

      if (dm_khois.length === 0) {
        dispatch(actions.categorySource.loadKhoiStart());
      }
      if (dm_cosos.length === 0) {
        dispatch(actions.categorySource.loadDmCoSoStart());
      }
    }, [dm_khois.length, dm_cosos.length, dispatch]);

    const generateEDStudentCode = useCallback(async (
      truongId: number,
      namNhapHoc: string,
      khoiId: number
    ) => {
      if (!truongId || !namNhapHoc || !khoiId) {
        return "";
      }

      const truong = allTruongs.find((t) => t.id === truongId);
      if (!truong) return "";

      const coSo = dm_cosos.find((c) => c.id === truong.dm_coso_id);
      const schoolCode = coSo?.campus_code || "01";

      const entryYearMatch = namNhapHoc.match(/\d{4}/);
      if (!entryYearMatch) return "";
      const entryYear = parseInt(entryYearMatch[0]);
      const entryYear2Digits = entryYear.toString().slice(-2);

      const khoi = dm_khois.find((k) => k.id === khoiId);
      const ten_khoi = khoi?.ten_khoi || "";
      const gradeMatch = ten_khoi.match(/\d+/);
      const gradeNumber = gradeMatch ? parseInt(gradeMatch[0]) : 1;

      const studyYears = 12 - gradeNumber + 1;
      const gradYear = entryYear + studyYears;
      const gradYear2Digits = gradYear.toString().slice(-2);

      const prefix = `ED${schoolCode}${entryYear2Digits}${gradYear2Digits}`;

      try {
        const targetCoSoId = truong.dm_coso_id || dm_coso_id;
        const response = await hocSinhApi.SelectAllMaHS(targetCoSoId);
        let nextSeq = 1;
        if (response.is_success && Array.isArray(response.data)) {
          const existingCodes: string[] = response.data;
          const campusPrefix = `ED${schoolCode}`;
          const matchingCodes = existingCodes.filter(
            (code) => code && code.startsWith(campusPrefix) && code.length === 12
          );

          let maxSeq = 0;
          matchingCodes.forEach((code) => {
            const suffix = code.slice(-4);
            if (suffix && /^\d+$/.test(suffix)) {
              const seq = parseInt(suffix);
              if (seq > maxSeq) {
                maxSeq = seq;
              }
            }
          });
          nextSeq = maxSeq + 1;
        }

        const seqStr = nextSeq.toString().padStart(4, "0");
        return `${prefix}${seqStr}`;
      } catch (error) {
        console.error("Error SelectAllMaHS:", error);
        return "";
      }
    }, [dm_coso_id, allTruongs, dm_khois, dm_cosos]);

    const generateAndSetOfficialCode = useCallback(async (
      truongId: number,
      namNhapHoc: string,
      khoiId: number
    ) => {
      if (!truongId || !namNhapHoc || !khoiId) return;
      setIsGeneratingCode(true);
      try {
        const newCode = await generateEDStudentCode(truongId, namNhapHoc, khoiId);
        if (newCode) {
          setValue("ma_hs", newCode);
          setFormData((prev) => ({
            ...prev,
            ma_hs: newCode,
          }));
          await trigger("ma_hs");
        }
      } catch (error) {
        console.error("Error auto-generating student code:", error);
      } finally {
        setIsGeneratingCode(false);
      }
    }, [generateEDStudentCode, setValue, trigger]);

    useEffect(() => {
      if (hocSinh) {
        const _data = buildNormalizedData(hocSinh);
        setFormData(_data);
        reset(_data);
      }
    }, [hocSinh, buildNormalizedData, reset]);

    useEffect(() => {
      if (hocSinh.id === 0 && formData.dm_truong_id && formData.nam_nhap_hoc && formData.dm_khoi_id) {
        generateAndSetOfficialCode(
          formData.dm_truong_id,
          formData.nam_nhap_hoc,
          formData.dm_khoi_id
        );
      }
    }, [
      hocSinh.id,
      formData.dm_truong_id,
      formData.nam_nhap_hoc,
      formData.dm_khoi_id,
      generateAndSetOfficialCode
    ]);

    const handleInvalid = useCallback(() => {
      NotifyHelper.Warning("Vui lòng điền đầy đủ các thông tin bắt buộc");
      setTimeout(() => {
        const container = formContainerRef.current;
        if (!container) return;
        const firstInvalid = container.querySelector('[aria-invalid="true"], [data-status="error"]');
        if (firstInvalid && typeof (firstInvalid as HTMLElement).scrollIntoView === "function") {
          (firstInvalid as HTMLElement).scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 50);
    }, []);

    const handleSaveChanges = async (data: any) => {
      const isValid = await trigger();
      if (!isValid) {
        handleInvalid();
        return;
      }

      setIsSaving(true);
      try {
        const finalMaHs = data.ma_hs ?? formData.ma_hs ?? "";
        const surveyResponeId = data.crm_survey_respone_id ?? formData.crm_survey_respone_id ?? hocSinh.crm_survey_respone_id ?? null;

        const formDataSubmit: HocSinhFormSubmitRequest = {
          id: data.id ?? formData.id ?? 0,
          ma_hs: finalMaHs,
          nam_hoc: data.nam_hoc || formData.nam_hoc || nam_hoc,
          ho_ten: data.ho_ten ?? "",
          ho_ten_en: data.ho_ten_en ?? "",
          ngay_sinh_timestamp: data.ngay_sinh
            ? new Date(data.ngay_sinh).getTime()
            : 0,
          dm_gioitinh_id: Number(data.dm_gioitinh_id) || 0,
          dia_chi_noi_sinh: data.dia_chi_noi_sinh ?? "",
          truong_hoc_cu: data.truong_hoc_cu ?? "",
          ho_ten_lien_he: data.ho_ten_lien_he ?? formData.ho_ten_lien_he ?? "",
          dien_thoai_lien_he: data.dien_thoai_lien_he ?? formData.dien_thoai_lien_he ?? "",
          email_lien_he: data.email_lien_he ?? formData.email_lien_he ?? "",
          dm_chiendich_id: Number(data.dm_chiendich_id ?? formData.dm_chiendich_id) || 0,
          dm_quanhe_id: Number(data.dm_quanhe_id) || 0,
          dm_nguonbiettoi_id: Number(data.dm_nguonbiettoi_id) || 0,
          dm_tinh_id: Number(data.dm_tinh_id) || 0,
          dm_huyen_id: Number(data.dm_huyen_id) || 0,
          dm_xa_id: Number(data.dm_xa_id) || 0,
          dia_chi_tt: data.dia_chi_tt ?? "",
          dm_truong_id: Number(data.dm_truong_id || formData.dm_truong_id) || 0,
          dm_khoi_id: Number(data.dm_khoi_id || formData.dm_khoi_id) || 0,
          dm_lop_id: Number(data.dm_lop_id || formData.dm_lop_id) || 0,
          dm_he_id: Number(data.dm_he_id || formData.dm_he_id) || 0,
          dm_quoctich_id: Number(data.dm_quoctich_id) || 0,
          dm_coso_id: Number(data.dm_coso_id || formData.dm_coso_id || dm_coso_id) || 0,
          ngaythi_dauvao_dukien: data.ngaythi_dauvao_dukien
            ? (moment(data.ngaythi_dauvao_dukien).format("YYYY-MM-DD") as any)
            : undefined,
          ngay_nhap_hoc: data.ngay_nhap_hoc
            ? (moment(data.ngay_nhap_hoc).format("YYYY-MM-DD") as any)
            : undefined,
          que_quan: data.que_quan ?? "",
          nam_nhap_hoc: data.nam_nhap_hoc ?? "",
          email_hocsinh: data.email_hocsinh ?? "",
          dien_thoai_hocsinh: data.dien_thoai_hocsinh ?? "",
          ma_so_bhxh: data.ma_so_bhxh ?? "",
          loai_dang_ky_bhxh: data.loai_dang_ky_bhxh ?? "",
          so_tien_bhxh: data.so_tien_bhxh ?? 0,
          noi_dang_ky_kcb_bandau: data.noi_dang_ky_kcb_bandau ?? "",
          doi_tuong_chinh_sach: data.doi_tuong_chinh_sach ?? "",
          ma_cua_bo: data.ma_cua_bo ?? "",
          chung_minh_thu_nguoilienhe: "",
          noi_cap_chung_minh_thu_nguoilienhe: "",
          ngay_cap_chung_minh_thu_nguoilienhe: undefined,
          ngay_sinh_nguoilienhe: undefined,
          quoc_tich_id_nguoilienhe: Number(data.dm_quoctich_id) || 0,
          nghe_nghiep_nguoilienhe: data.nghe_nghiep ?? formData.nghe_nghiep ?? "",
          noi_lam_viec_nguoilienhe: data.noi_lam_viec ?? formData.noi_lam_viec ?? "",
          dia_chi_thuong_tru_nguoilienhe: "",
          dm_dantoc_id: Number(data.dm_dantoc_id) || 0,
          dm_tongiao_id: Number(data.dm_tongiao_id) || 0,
          ma_dinh_danh: data.ma_dinh_danh ?? "",
          tt_suc_khoe: data.tt_suc_khoe ?? "",
          dia_chi_hientai: data.dia_chi_hientai ?? "",
          chieu_cao: data.chieu_cao ?? "",
          can_nang: data.can_nang ?? "",
          so_dien_thoai_bo: "",
          so_dien_thoai_me: "",
          ho_chieu: data.ho_chieu ?? "",
          ngay_ketthuc_hoc: data.ngay_ketthuc_hoc ?? undefined,
          ma_dinh_danh_csdl: data.ma_dinh_danh_csdl ?? "",
          ma_so_bhyt: data.ma_so_bhyt ?? "",
          ghi_chu_chung: data.ghi_chu_chung ?? "",
          dm_trangthaihocsinh_id: Number(data.dm_trangthaihocsinh_id) || 0,
          is_nhucau_noitru: data.is_nhucau_noitru ?? false,
          laconthu: (!data.laconthu && data.laconthu !== "") ? data.laconthu : undefined,
          ma_kh: data.ma_kh ?? "",
          ma_cu: data.ma_cu ?? "",
          dm_tinh_ht_id: data.dm_tinh_ht_id,
          dm_huyen_ht_id: data.dm_huyen_ht_id,
          dm_xa_ht_id: data.dm_xa_ht_id,
          dieu_phoi_vien_id: data.dieu_phoi_vien_id,
          luu_y_suc_khoe: data.luu_y_suc_khoe ?? "",
          luu_y_an_uong: data.luu_y_an_uong ?? "",
          tinh_trang_ban_tru: data.tinh_trang_ban_tru ?? "",
          dang_ky_bus: data.dang_ky_bus ?? "",
          hinh_thuc_don_tra: data.hinh_thuc_don_tra ?? "",
          gui_xe_dap: data.gui_xe_dap ?? "",
          crm_survey_respone_id: surveyResponeId,
        };

        const res = formData.id > 0
          ? await crmHocSinhApi.update(formDataSubmit)
          : await crmHocSinhApi.insert(formDataSubmit);

        if (res.is_success) {
          NotifyHelper.Success(
            formData.id > 0
              ? "Cập nhật thành công"
              : (surveyResponeId ? "Tiếp nhận học sinh thành công" : "Thêm mới thành công")
          );
          onSubmitSuccess(res.data);
        } else {
          NotifyHelper.Error(res.message ?? "");
        }
      } finally {
        setIsSaving(false);
      }
    };

    useImperativeHandle(ref, () => ({
      handleSaveChanges: () => {
        handleSubmit(handleSaveChanges, handleInvalid)();
      },
    }));

    return (
      <form ref={formContainerRef} onSubmit={handleSubmit(handleSaveChanges, handleInvalid)}>
        <Box sx={{
          display: "grid",
          gap: "24px",
          position: "relative",
        }}>
          <ThongTinHocSinh
            control={control}
            hocSinh={hocSinh}
            setFormData={setFormData}
            setValue={setValue}
            errors={errors}
            register={register}
            formData={formData}
            trigger={trigger}
            onReloadMaHs={() => generateAndSetOfficialCode(
              formData.dm_truong_id,
              formData.nam_nhap_hoc,
              formData.dm_khoi_id
            )}
            isReloadingMaHs={isGeneratingCode}
          />
          <ThongTinDangKy
            control={control}
            hocSinh={hocSinh}
            setFormData={setFormData}
            setValue={setValue}
            errors={errors}
            register={register}
            formData={formData}
            trigger={trigger}
          />
          <ThongTinDiaChi
            control={control}
            hocSinh={hocSinh}
            setFormData={setFormData}
            setValue={setValue}
            errors={errors}
            register={register}
            formData={formData}
            trigger={trigger}
          />
          <ThongTinKhac
            control={control}
            hocSinh={hocSinh}
            setFormData={setFormData}
            setValue={setValue}
            errors={errors}
            register={register}
            formData={formData}
            trigger={trigger}
          />
          <ThongTinLienHe
            control={control}
            hocSinh={hocSinh}
            setFormData={setFormData}
            setValue={setValue}
            errors={errors}
            register={register}
            formData={formData}
            trigger={trigger}
          />
          {!hideSaveButton && (
            <Box sx={{
              backdropFilter: "blur(1px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 2,
              position: "sticky",
              bottom: 0,
            }}>
              <Button
                text="Base.Label.Save"
                variant="primary"
                type="submit"
                size="large"
                block
                isLoading={isSaving}
                sx={{
                  width: "250px",
                  boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px;",
                  borderRadius: "32px",
                }}
              />
            </Box>
          )}
        </Box>
      </form>
    );
  }
);

export default ProfileForm;
